import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { 
    AUCTION_START_DATE,
    AUCTION_DAYS,
    TOTAL_DAYS,
    SOL_PRICE_USD,
    TOTAL_AUCTION_TOKENS,
    DAILY_ALLOCATION,
    getDailyAllocation,
    getBonusMultiplier,
    CIRCULATING_SUPPLY,
    START_PRICE
} from '../utils/constants';
import { 
    getCurrentTargetDate, 
    getDayEndDate, 
    getDayStartDate,
    calculateCurrentDay 
} from '../utils/timerUtils';
import { 
    getSimulatedData, 
    TOTAL_SIMULATED_USERS, 
    TOTAL_SIMULATED_SOL, 
    useSimulatedData 
} from '../utils/simulatedData';
import { createStatsCalculator, CONSTANTS } from '../utils/statsCalculator';
import priceCalculator from '../utils/priceCalculator';

// Create a new instance of StatsCalculator
const StatsCalculator = createStatsCalculator();

// Helper functions
const isTaskCompleted = (platform, task) => {
    // TODO: Implement actual task completion check
    return false;
};

// Create the context
export const AuctionContext = createContext();

export const useAuction = () => {
    const context = useContext(AuctionContext);
    if (!context) {
        throw new Error('useAuction must be used within an AuctionProvider');
    }
    return context;
}

export const AuctionProvider = ({ children }) => {
    // Constants
    const TOTAL_JINU_SUPPLY = 37500000; // 37.5M JINU
    const LAMPORTS_PER_SOL = 1000000000;

    // Helper function to calculate current day
    const calculateCurrentDay = () => {
        const now = new Date();
        const startDate = new Date(AUCTION_START_DATE);
        const diffTime = Math.abs(now - startDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return Math.min(Math.max(1, diffDays), TOTAL_DAYS);
    };

    // Calculate daily values based on day number
    const calculateDailyValues = (day) => {
        if (!useSimulatedData) {
            return {
                totalAmount: 0,
                totalUsers: 0,
                simulatedParticipants: 0
            };
        }

        const { users, sol } = getSimulatedData(day);
        const allocation = getDailyAllocation(day);
        
        // Log the values for each day
        console.log(`${day.toString().padStart(2, '0')} | ${users.toString().padStart(3, ' ')} | ${sol.toFixed(2)}`);
        
        return {
            totalAmount: sol,
            totalUsers: users,
            simulatedParticipants: users,
            allocation
        };
    };

    // Calculate auction stats including simulated data
    const calculateAuctionStats = () => {
        // Calculate total SOL raised from simulated data
        const simulatedTotal = Array.from({ length: AUCTION_DAYS }, (_, i) => i + 1)
            .reduce((total, day) => {
                // Get simulated data for this day
                const simData = getSimulatedData(day);
                return total + (simData.sol || 0);
            }, 0);
            
        // Calculate total user contributions
        const userTotal = Object.entries(userContributions)
            .reduce((total, [day, contributions]) => {
                return total + contributions.reduce((dayTotal, contrib) => dayTotal + contrib.amount, 0);
            }, 0);
            
        // Calculate DEX price based on total SOL
        const dexPrice = priceCalculator.calculate(simulatedTotal + userTotal);
        
        // Calculate total participants - use the constant for simulated users
        const hasUserContributions = Object.values(userContributions).some(dayContribs => dayContribs.length > 0);
        const totalParticipants = hasUserContributions ? 
            TOTAL_SIMULATED_USERS + 1 : TOTAL_SIMULATED_USERS;
            
        console.log("Auction stats calculation:", {
            simulatedTotal,
            userTotal,
            totalSolRaised: simulatedTotal + userTotal,
            totalParticipants,
            hasUserContributions,
            userContributionsCheck: Object.values(userContributions).some(dayContribs => dayContribs.length > 0)
        });
            
        return {
            totalSolRaised: simulatedTotal + userTotal,
            simulatedTotal,
            userTotal,
            totalParticipants,
            totalSold: simulatedTotal + userTotal, // This is what's displayed in the UI
            ...dexPrice
        };
    };

    // Initialize state
    const [state, setState] = useState({
        isInitialized: false,
        isLoading: true,
        error: null,
        auctionState: {
            currentDay: AUCTION_DAYS,
            totalSolRaised: TOTAL_SIMULATED_SOL,
            totalSold: TOTAL_SIMULATED_SOL,
            totalParticipants: TOTAL_SIMULATED_USERS,
            userContributions: {},
            totalContributions: 0,
            dailyAllocations: {}
        },
        selectedDay: calculateCurrentDay(),
        isDemo: true, // Set demo mode as default
        projectedContributions: {},
        pendingContribution: 0,
        stats: {
            totalSolRaised: TOTAL_SIMULATED_SOL,
            currentPrice: 0,
            dexPrice: 0,
            marketCap: 0,
            moonshot: 0,
            rewardsPool: 0,
            confirmedJinu: 0,
            pendingJinu: 0,
            totalContribution: 0,
            pendingContribution: 0,
            daysParticipated: 0,
            sharePercentage: 0,
            hasConfirmedContributions: false,
            hasProjectedContributions: false
        }
    });

    const { auctionState, selectedDay, isDemo, pendingContribution, projectedContributions, stats } = state;
    const { dailyAllocations, userContributions, totalContributions, totalParticipants } = auctionState;
    const { totalSolRaised, rewardsPool } = stats || {};
    const currentDay = calculateCurrentDay();

    const wallet = useWallet();

    const [selectedDayState, setSelectedDayState] = useState(null);

    useEffect(() => {
        // Update current day based on time
        const updateCurrentDay = () => {
            const currentDay = calculateCurrentDay();
            setState(prevState => ({
                ...prevState,
                selectedDay: currentDay,
                auctionState: {
                    ...prevState.auctionState,
                    currentDay
                }
            }));
        };

        // Update immediately and set interval
        updateCurrentDay();
        const interval = setInterval(updateCurrentDay, 1000);

        return () => clearInterval(interval);
    }, []);

    const addToRewardsPool = (amount) => {
        setState(prevState => ({ ...prevState, rewardsPool: prevState.rewardsPool + amount }));
    };

    // Initialize auction state with simulated data
    useEffect(() => {
        // Only initialize once
        if (Object.keys(auctionState.dailyAllocations).length > 0) {
            return;
        }
        
        // Check if we already have simulated data in localStorage
        const storedSimulatedData = localStorage.getItem('simulatedAuctionData');
        
        if (storedSimulatedData) {
            // Use stored data if available
            const parsedData = JSON.parse(storedSimulatedData);
            
            setState(prevState => ({
                ...prevState,
                totalSolRaised: parsedData.totalSolRaised,
                stats: calculateAuctionStats(),
                auctionState: {
                    ...prevState.auctionState,
                    dailyAllocations: parsedData.dailyAllocations,
                    totalSolRaised: parsedData.totalSolRaised
                }
            }));
            
            return;
        }
        
        // If no stored data, generate new simulated data
        
        // Initialize StatsCalculator with simulated data
        StatsCalculator.init();
        
        // Add simulated contributions for each day with consistent values
        for (let day = 1; day <= AUCTION_DAYS; day++) {
            // This will use the seeded random function in StatsCalculator
            // which now generates consistent values based on the day number
            StatsCalculator.getSimulatedContribution(day);
        }
        
        // Get total SOL raised including simulated data
        const totalSolRaised = Array.from({ length: AUCTION_DAYS }, (_, i) => i + 1)
            .reduce((total, day) => total + (StatsCalculator.getDailyTotal(day) || 0), 0);
            
        // Initialize daily allocations
        // Calculate base JINU allocation per day
        const TOTAL_JINU = 37500000; // 37.5M JINU
        const baseJinuPerDay = TOTAL_JINU / AUCTION_DAYS;
        
        // Calculate daily allocations with ±35% variance using consistent seed
        const dailyAllocations = Array(TOTAL_DAYS).fill(null).map((_, index) => {
            const day = index + 1;
            
            // Get simulated data from StatsCalculator
            const totalAmount = StatsCalculator.getDailyTotal(day) || 0;
            
            // Generate consistent user count based on day number
            const userSeed = Math.sin(day * 500) * 10000;
            const totalUsers = Math.floor((userSeed - Math.floor(userSeed)) * 50) + 20; // Random number between 20-70
            
            // Calculate JINU allocation with ±35% variance using consistent seed
            const varianceSeed = Math.sin(day * 200) * 10000;
            const variance = ((varianceSeed - Math.floor(varianceSeed)) * 0.7) - 0.35; // Random number between -0.35 and 0.35
            const jinuAllocation = baseJinuPerDay * (1 + variance);
            
            return {
                day,
                totalAmount,
                totalUsers,
                userParticipation: [],
                userContributions: {},
                totalParticipants: totalUsers,
                totalContributions: totalAmount,
                bonusMultiplier: getBonusMultiplier(day),
                allocation: totalAmount,
                jinuAllocation, // JINU allocation with ±35% variance
                jinuVariance: variance * 100, // Store variance percentage for reference
                startDate: getDayStartDate(day),
                endDate: getDayEndDate(day)
            };
        });

        // Verify total JINU allocation matches 37.5M
        const totalJinuAllocated = dailyAllocations.reduce((sum, day) => sum + day.jinuAllocation, 0);
        const adjustmentFactor = TOTAL_JINU / totalJinuAllocated;
        
        // Adjust allocations to ensure total is exactly 37.5M JINU
        dailyAllocations.forEach(day => {
            day.jinuAllocation = day.jinuAllocation * adjustmentFactor;
        });

        // Convert to object format for state
        const dailyAllocationsObj = dailyAllocations.reduce((acc, allocation) => {
            acc[allocation.day] = allocation;
            return acc;
        }, {});
        
        // Store the simulated data in localStorage for future use
        localStorage.setItem('simulatedAuctionData', JSON.stringify({
            dailyAllocations: dailyAllocationsObj,
            totalSolRaised
        }));

        setState(prevState => ({
            ...prevState,
            totalSolRaised,
            stats: calculateAuctionStats(),
            auctionState: {
                ...prevState.auctionState,
                dailyAllocations: dailyAllocationsObj,
                totalSolRaised
            }
        }));
    }, []);

    const handleContribute = async (amount, day, type = 'manual') => {
        if (!wallet.connected || !amount || amount <= 0) return false;

        try {
            // Calculate the new total SOL raised immediately
            const simulatedTotal = TOTAL_SIMULATED_SOL;
            
            // Get current user contributions
            const currentUserContributions = { ...userContributions };
            
            // Initialize array if it doesn't exist
            if (!currentUserContributions[day]) {
                currentUserContributions[day] = [];
            }
            
            // Calculate current user total before this contribution
            const currentUserTotal = Object.entries(currentUserContributions)
                .reduce((total, [d, contributions]) => {
                    return total + contributions.reduce((dayTotal, contrib) => dayTotal + contrib.amount, 0);
                }, 0);
                
            // Calculate new total with this contribution
            const newTotalSolRaised = simulatedTotal + currentUserTotal + parseFloat(amount);
            
            console.log("Direct calculation in handleContribute:", {
                simulatedTotal,
                currentUserTotal,
                newContribution: parseFloat(amount),
                newTotalSolRaised
            });
            
            // Update state with the contribution
            setState(prevState => {
                const newUserContributions = { ...prevState.auctionState.userContributions };
                
                // Initialize arrays if they don't exist
                if (!newUserContributions[day]) {
                    newUserContributions[day] = [];
                }
                
                // Add new contribution
                newUserContributions[day].push({
                    amount: parseFloat(amount),
                    timestamp: Date.now(),
                    type: type // Use the type parameter
                });
                
                // Calculate total user contributions after adding this contribution
                const totalUserContributions = Object.values(newUserContributions)
                    .reduce((acc, dayContribs) => 
                        acc + dayContribs.reduce((dayAcc, contrib) => dayAcc + contrib.amount, 0), 0);
                
                // Clear any projected contributions
                const newProjectedContributions = { ...prevState.projectedContributions };
                delete newProjectedContributions[day];
                
                // Calculate JINU for this contribution
                const dayStats = getDayStatsWithProjection(day, 0); // No projection since it's confirmed
                const jinuFromContribution = dayStats.jinuAmount || 0;
                
                // Calculate days participated
                const participatedDays = Object.keys(newUserContributions).length;
                
                // Always add 1 to simulated participants when user has made any contribution
                const totalParticipants = TOTAL_SIMULATED_USERS + 1;
                
                // Create a new state object to force re-render
                const updatedState = {
                    ...prevState,
                    auctionState: {
                        ...prevState.auctionState,
                        userContributions: newUserContributions,
                        totalContributions: totalUserContributions,
                        totalSolRaised: newTotalSolRaised, // Direct update with calculated value
                        totalSold: newTotalSolRaised, // Make sure this is updated too
                        totalParticipants: totalParticipants // Always add 1 for the real user
                    },
                    projectedContributions: newProjectedContributions,
                    pendingContribution: 0,
                    stats: {
                        ...prevState.stats,
                        totalSolRaised: newTotalSolRaised, // Direct update with calculated value
                        confirmedJinu: (prevState.stats?.confirmedJinu || 0) + jinuFromContribution,
                        pendingJinu: 0,
                        totalContribution: totalUserContributions,
                        pendingContribution: 0,
                        daysParticipated: participatedDays,
                        hasConfirmedContributions: true
                    }
                };
                
                // Log the updated state for debugging
                console.log("Updated state in handleContribute:", {
                    totalSolRaised: updatedState.auctionState.totalSolRaised,
                    totalUserContributions,
                    totalSold: updatedState.auctionState.totalSold,
                    totalParticipants: updatedState.auctionState.totalParticipants
                });
                
                return updatedState;
            });

            return true;
        } catch (error) {
            console.error('Error in handleContribute:', error);
            return false;
        }
    };

    const handleReserveAllDays = async (amount) => {
        if (!wallet.connected || !amount || amount <= 0) return false;

        // In demo mode, don't check wallet balance
        if (!isDemo) {
            try {
                // Check if user has enough balance - with proper error handling
                if (!wallet.adapter || !wallet.publicKey) {
                    console.error('Wallet not properly connected');
                    return false;
                }
                
                // Use the connection safely
                const connection = wallet.adapter.connection;
                if (!connection) {
                    console.error('No connection available from wallet adapter');
                    return false;
                }
                
                const balance = await connection.getBalance(wallet.publicKey);
                const lamports = balance / LAMPORTS_PER_SOL;
                if (lamports < amount) {
                    console.error('Insufficient balance');
                    return false;
                }
            } catch (error) {
                console.error('Error checking wallet balance:', error);
                return false;
            }
        }

        try {
            // Parse amount to ensure it's a number
            const parsedAmount = parseFloat(amount);
            
            console.log("Starting handleReserveAllDays with amount:", parsedAmount);
            
            // First, clear any projected contributions
            setState(prevState => ({
                ...prevState,
                projectedContributions: {}
            }));
            
            // Update state with the contribution for each remaining day
            setState(prevState => {
                // Calculate the new total SOL raised immediately
                const simulatedTotal = TOTAL_SIMULATED_SOL;
                
                // Get current user contributions before adding new ones
                const currentUserContributions = JSON.parse(JSON.stringify(prevState.auctionState.userContributions));
                
                // Calculate current user total before this contribution
                const currentUserTotal = Object.entries(currentUserContributions)
                    .reduce((total, [d, contributions]) => {
                        return total + contributions.reduce((dayTotal, contrib) => dayTotal + contrib.amount, 0);
                    }, 0);
                
                console.log("Current user total before contribution:", currentUserTotal);
                
                // Calculate new total with this contribution
                const newTotalSolRaised = simulatedTotal + currentUserTotal + parsedAmount;
                
                console.log("Direct calculation in handleReserveAllDays:", {
                    simulatedTotal,
                    currentUserTotal,
                    newContribution: parsedAmount,
                    newTotalSolRaised
                });
                
                // Calculate amount per day
                const remainingDays = AUCTION_DAYS - currentDay + 1;
                const amountPerDay = parsedAmount / remainingDays;
                
                console.log("Distributing", parsedAmount, "SOL across", remainingDays, "days at", amountPerDay, "SOL per day");
                
                // Create a new user contributions object
                const newUserContributions = { ...currentUserContributions };
                
                // Track total JINU tokens from this contribution
                let totalJinuFromContribution = 0;
                
                // Add contribution for each remaining day
                for (let i = currentDay; i <= AUCTION_DAYS; i++) {
                    if (!newUserContributions[i]) {
                        newUserContributions[i] = [];
                    }
                    
                    // Add new contribution
                    newUserContributions[i].push({
                        amount: amountPerDay,
                        timestamp: Date.now(),
                        type: 'all'  // Mark as 'all' to indicate it's from "all days" contribution
                    });
                    
                    // Calculate JINU for this day
                    const dayStats = getDayStatsWithProjection(i, 0); // No projection needed since it's confirmed
                    totalJinuFromContribution += dayStats.jinuAmount || 0;
                }
                
                // Calculate days participated
                const participatedDays = Object.keys(newUserContributions).length;
                
                // Calculate total user contributions after adding this contribution
                const totalUserContributions = Object.values(newUserContributions)
                    .reduce((acc, dayContribs) => 
                        acc + dayContribs.reduce((dayAcc, contrib) => dayAcc + contrib.amount, 0), 0);
                
                // Explicitly set totalParticipants
                const totalParticipants = TOTAL_SIMULATED_USERS + 1;
                
                console.log("Updated state in handleReserveAllDays:", {
                    totalSolRaised: newTotalSolRaised,
                    totalUserContributions,
                    totalParticipants
                });
                
                return {
                    ...prevState,
                    stats: {
                        ...prevState.stats,
                        totalSolRaised: newTotalSolRaised,
                        confirmedJinu: (prevState.stats?.confirmedJinu || 0) + totalJinuFromContribution,
                        pendingJinu: 0, // Clear pending since we're confirming all
                        totalContribution: totalUserContributions,
                        pendingContribution: 0,
                        daysParticipated: participatedDays,
                        hasConfirmedContributions: true
                    },
                    auctionState: {
                        ...prevState.auctionState,
                        userContributions: newUserContributions,
                        totalSolRaised: newTotalSolRaised,
                        totalSold: newTotalSolRaised,
                        totalParticipants: totalParticipants
                    }
                };
            });

            return true;
        } catch (error) {
            console.error('Error in handleReserveAllDays:', error);
            return false;
        }
    };

    const toggleDemoMode = () => {
        setState(prevState => ({
            ...prevState,
            isDemo: !prevState.isDemo
        }));
    };

    // Calculate projected stats based on user input
    const calculateProjectedStats = (amount, day = null) => {
        if (!amount || amount <= 0) {
            clearProjectedContributions();
            return;
        }

        const numAmount = parseFloat(amount);
        
        setState(prevState => {
            let newProjectedContributions = { ...prevState.projectedContributions };
            let totalPendingJinu = 0;
            let confirmedJinu = prevState.stats?.confirmedJinu || 0;
            
            if (day) {
                // Single day contribution
                newProjectedContributions[day] = numAmount;
                
                // Calculate JINU for this specific day
                const dayStats = getDayStatsWithProjection(day, numAmount);
                
                // Use only the projected JINU amount, not the total
                totalPendingJinu = dayStats.projectedJinu || 0;
                
                console.log(`Day ${day} stats:`, {
                    totalDayContributions: dayStats.totalAmount,
                    userContribution: dayStats.totalContribution,
                    projectedAmount: dayStats.projectedContribution,
                    totalSharePercentage: dayStats.sharePercentage,
                    projectedSharePercentage: dayStats.projectedSharePercentage,
                    dayJinuAllocation: dayStats.dayAllocation,
                    confirmedJinu: dayStats.confirmedJinu,
                    projectedJinu: dayStats.projectedJinu
                });

                return {
                    ...prevState,
                    pendingContribution: numAmount,
                    projectedContributions: newProjectedContributions,
                    stats: {
                        ...prevState.stats,
                        pendingJinu: totalPendingJinu,
                        confirmedJinu: confirmedJinu,
                        projectedJinu: confirmedJinu + totalPendingJinu
                    }
                };
            } else {
                // All days contribution - distribute evenly
                const remainingDays = AUCTION_DAYS - currentDay + 1;
                const amountPerDay = numAmount / remainingDays;
                
                // Clear any existing projections
                newProjectedContributions = {};
                
                // Add projection for each remaining day
                for (let i = currentDay; i <= AUCTION_DAYS; i++) {
                    newProjectedContributions[i] = amountPerDay;
                    const dayStats = getDayStatsWithProjection(i, amountPerDay);
                    
                    // Use only the projected JINU amount, not the total
                    totalPendingJinu += dayStats.projectedJinu || 0;
                    
                    console.log(`Day ${i} stats:`, {
                        totalDayContributions: dayStats.totalAmount,
                        userContribution: dayStats.totalContribution,
                        projectedAmount: dayStats.projectedContribution,
                        totalSharePercentage: dayStats.sharePercentage,
                        projectedSharePercentage: dayStats.projectedSharePercentage,
                        dayJinuAllocation: dayStats.dayAllocation,
                        confirmedJinu: dayStats.confirmedJinu,
                        projectedJinu: dayStats.projectedJinu
                    });
                }
                
                // Ensure we have valid numbers
                totalPendingJinu = Math.max(0, totalPendingJinu);
                confirmedJinu = Math.max(0, confirmedJinu);
                
                return {
                    ...prevState,
                    pendingContribution: numAmount,
                    projectedContributions: newProjectedContributions,
                    stats: {
                        ...prevState.stats,
                        pendingJinu: totalPendingJinu,
                        confirmedJinu: confirmedJinu,
                        projectedJinu: confirmedJinu + totalPendingJinu
                    }
                };
            }
        });
    };
    
    // Helper function to get day stats with a specific projection amount
    const getDayStatsWithProjection = (day, projectedAmount = 0) => {
        // Get simulated data for this day
        const simData = getSimulatedData(day);
        
        // Get confirmed contributions for this day
        const confirmedContributions = userContributions[day] || [];
        const userContribution = confirmedContributions.reduce((acc, contrib) => acc + contrib.amount, 0);
        
        // Get simulated SOL for this day
        const simulatedSol = simData.sol || 0;
        
        // Calculate total contributions including simulated data
        const totalDayContributions = simulatedSol + userContribution;
        const totalWithProjected = totalDayContributions + projectedAmount;
        
        // Calculate user's total contribution including projected
        const totalUserContribution = userContribution + projectedAmount;
        
        // Calculate share percentages (with and without projected)
        const confirmedSharePercentage = totalDayContributions > 0 
            ? (userContribution / totalDayContributions) * 100 
            : 0;
        
        // For projected contributions, calculate based on the new projected amount only
        // This ensures that each new contribution is calculated correctly
        const projectedSharePercentage = projectedAmount > 0
            ? (projectedAmount / totalWithProjected) * 100
            : 0;
            
        // Calculate JINU amount based on day allocation and share percentage
        const dayJinuAllocation = simData.jinu || 0;
        const confirmedJinu = (dayJinuAllocation * confirmedSharePercentage) / 100;
        const projectedJinu = (dayJinuAllocation * projectedSharePercentage) / 100;
        const totalJinu = confirmedJinu + projectedJinu;
        
        console.log(`Day ${day} share calculation:`, {
            simulatedSol,
            userContribution,
            projectedAmount,
            totalDayContributions,
            totalWithProjected,
            confirmedSharePercentage,
            projectedSharePercentage,
            dayJinuAllocation,
            confirmedJinu,
            projectedJinu,
            totalJinu
        });
        
        return {
            day,
            totalAmount: totalDayContributions,
            totalWithProjected,
            totalContribution: userContribution,
            projectedContribution: projectedAmount,
            sharePercentage: confirmedSharePercentage,
            projectedSharePercentage,
            dayAllocation: dayJinuAllocation,
            confirmedJinu,
            projectedJinu,
            jinuAmount: totalJinu
        };
    };

    const getDayStats = (day) => {
        // Get simulated data for this day
        const simData = getSimulatedData(day);
        
        // Get user contribution for this day
        const userContribution = userContributions[day]?.reduce((total, contrib) => 
            total + contrib.amount, 0) || 0;

        // Get projected contribution for this day
        const projectedContribution = projectedContributions[day] || 0;
        const totalUserContribution = userContribution + projectedContribution;

        // Get the day's JINU allocation from simulated data
        const dayAllocation = simData.jinu || 0;
        
        // Calculate total SOL for this day (simulated + user contributions)
        const simulatedSol = simData.sol || 0;
        const totalContributionsForDay = simulatedSol + userContribution;
        const totalWithProjected = totalContributionsForDay + projectedContribution;
        
        // Calculate share percentage based on total contributions including projections
        const sharePercentage = totalWithProjected > 0 
            ? (totalUserContribution / totalWithProjected) * 100 
            : 0;

        // Calculate JINU amount based on day allocation and share percentage
        const jinuAmount = dayAllocation && sharePercentage > 0
            ? (dayAllocation * sharePercentage) / 100 
            : 0;

        return {
            totalContribution: userContribution,
            projectedContribution: projectedContribution,
            totalUserContribution,
            sharePercentage: parseFloat(sharePercentage.toFixed(2)),
            jinuAmount: parseFloat(jinuAmount.toFixed(2)),
            totalAmount: totalWithProjected,
            projectedTokens: parseFloat(jinuAmount.toFixed(2)),
            dayAllocation: dayAllocation,
            hasConfirmedContribution: userContribution > 0,
            hasPendingContribution: projectedContribution > 0
        };
    };

    const calculateConfirmedJinu = (contributions) => {
        let totalConfirmedJinu = 0;
        
        Object.entries(contributions).forEach(([day, dayContributions]) => {
            // Get this day's allocation data
            const dayAllocation = dailyAllocations[day];
            if (!dayAllocation) return;
            
            // Calculate user's contribution for this day
            const userContribution = dayContributions.reduce((acc, contrib) => acc + contrib.amount, 0);
            
            // Get total contributions for this day
            const totalDayContributions = dayAllocation.totalContributions || 0;
            
            // Calculate share percentage
            const sharePercentage = totalDayContributions > 0 
                ? (userContribution / totalDayContributions) * 100 
                : 0;
            
            // Get this day's JINU allocation and calculate user's share
            const dayJinuAllocation = dayAllocation.jinuAllocation || 0;
            const dayJinu = (dayJinuAllocation * sharePercentage) / 100;
            
            totalConfirmedJinu += dayJinu;
        });
        
        return totalConfirmedJinu;
    };

    const calculatePendingJinu = (projectedContribs) => {
        let totalPendingJinu = 0;
        
        Object.entries(projectedContribs).forEach(([day, amount]) => {
            if (amount > 0) {
                day = parseInt(day);
                // Get day stats with this projection
                const dayStats = getDayStatsWithProjection(day, amount);
                totalPendingJinu += dayStats.projectedJinu || 0;
            }
        });

        return totalPendingJinu;
    };

    // Helper function to get day stats without projections (for confirmed calculations)
    const getDayStatsWithoutProjection = (day) => {
        // Reuse getDayStatsWithProjection with 0 projected amount
        return getDayStatsWithProjection(day, 0);
    };

    // Get overall user stats
    const getUserStats = () => {
        if (!wallet.connected) return null;

        // Calculate total confirmed contribution
        let totalConfirmedContribution = 0;
        let totalConfirmedJinu = 0;
        let hasConfirmedContributions = false;

        // Calculate days participated
        const participatedDays = [];
        let hasAllDaysContribution = false;

        console.log("getUserStats - Current userContributions:", userContributions);

        // Check each day for contributions
        for (let day = 1; day <= AUCTION_DAYS; day++) {
            const dayContributions = userContributions[day] || [];
            
            if (dayContributions.length > 0) {
                // Add up confirmed contributions
                const dayTotal = dayContributions.reduce((sum, contrib) => {
                    console.log(`Day ${day} contribution:`, contrib);
                    return sum + contrib.amount;
                }, 0);
                
                console.log(`Day ${day} total:`, dayTotal);
                totalConfirmedContribution += dayTotal;
                
                // Check for all days contribution
                if (dayContributions.some(c => c.type === 'all' || c.type === 'both')) {
                    hasAllDaysContribution = true;
                }
                
                // Add to participated days if any contribution type
                participatedDays.push(day);
                
                // Calculate JINU for this day
                const dayStats = getDayStatsWithoutProjection(day);
                totalConfirmedJinu += dayStats.jinuAmount;
                
                hasConfirmedContributions = true;
            }
        }
        
        console.log("getUserStats - totalConfirmedContribution:", totalConfirmedContribution);

        // Calculate pending JINU from projected contributions
        let totalPendingJinu = 0;
        let totalPendingContribution = 0;
        
        // Calculate pending JINU for each day with projected contributions
        Object.entries(projectedContributions).forEach(([day, amount]) => {
            if (amount > 0) {
                day = parseInt(day);
                totalPendingContribution += amount;
                
                // Get day stats with this projection
                const dayStats = getDayStatsWithProjection(day, amount);
                totalPendingJinu += dayStats.projectedJinu || 0;
            }
        });

        // Calculate days participated - just count the actual days with contributions
        const daysParticipated = participatedDays.length;

        // Calculate share percentage of total auction - use the total from calculateAuctionStats
        const auctionStats = calculateAuctionStats();
        const totalSol = auctionStats.totalSolRaised;
        
        const sharePercentage = totalSol > 0 
            ? (totalConfirmedContribution / totalSol) * 100 
            : 0;
            
        // Calculate bonus based on contribution amount
        let bonus = 0;
        if (totalConfirmedContribution >= 50) bonus = 15;
        else if (totalConfirmedContribution >= 20) bonus = 10;
        else if (totalConfirmedContribution >= 10) bonus = 7.5;
        else if (totalConfirmedContribution >= 5) bonus = 5;
        else if (totalConfirmedContribution >= 1) bonus = 1;

        // Add bonus from current phase
        const phaseBonus = getBonusMultiplier(currentDay);
            
        console.log("getUserStats - Final calculations:", {
            totalConfirmedContribution,
            totalPendingContribution,
            daysParticipated,
            sharePercentage,
            bonus,
            phaseBonus
        });

        return {
            totalContribution: totalConfirmedContribution,
            pendingContribution: totalPendingContribution,
            sharePercentage: parseFloat(sharePercentage.toFixed(2)),
            daysParticipated,
            confirmedJinu: parseFloat(totalConfirmedJinu.toFixed(2)),
            pendingJinu: parseFloat(totalPendingJinu.toFixed(2)),
            projectedJinu: parseFloat((totalConfirmedJinu + totalPendingJinu).toFixed(2)),
            currentBonus: bonus + phaseBonus,
            bonusBadge: getBonusBadge(bonus),
            hasConfirmedContributions,
            hasAllDaysContribution,
            hasProjectedContributions: totalPendingContribution > 0
        };
    };

    const getBonusBadge = (bonus) => {
        if (bonus >= 15) return 'Diamond';
        if (bonus >= 10) return 'Platinum';
        if (bonus >= 7.5) return 'Gold';
        if (bonus >= 5) return 'Silver';
        if (bonus >= 1) return 'Bronze';
        return 'None';
    };

    // Clear projected contributions
    const clearProjectedContributions = () => {
        setState(prevState => ({
            ...prevState,
            pendingContribution: 0,
            projectedContributions: {},
            stats: {
                ...prevState.stats,
                pendingJinu: 0,
                projectedJinu: prevState.stats.confirmedJinu || 0
            }
        }));
    };

    // Update stats when user contributions change
    useEffect(() => {
        if (wallet.connected) {
            const newStats = {
                totalContribution: Object.values(userContributions).reduce((acc, val) => acc + val, 0),
                pendingContribution: Object.values(projectedContributions).reduce((acc, val) => acc + val, 0),
                confirmedJinu: calculateConfirmedJinu(userContributions),
                pendingJinu: calculatePendingJinu(projectedContributions),
                hasConfirmedContributions: Object.values(userContributions).some(val => val > 0),
                hasProjectedContributions: Object.values(projectedContributions).some(val => val > 0),
                sharePercentage: calculateSharePercentage(userContributions, projectedContributions),
                daysParticipated: Object.keys(userContributions).length
            };

            setState(prevState => ({
                ...prevState,
                stats: {
                    ...prevState.stats,
                    ...newStats
                }
            }));
        }
    }, [wallet.connected, userContributions, projectedContributions]);

    const calculateSharePercentage = (confirmedContribs, projectedContribs) => {
        // Calculate total confirmed contribution
        const totalConfirmed = Object.values(confirmedContribs).reduce((acc, val) => acc + val, 0);
        
        // Calculate total projected contribution
        const totalProjected = Object.values(projectedContribs).reduce((acc, val) => acc + val, 0);
        
        // Get total SOL raised
        const totalSol = Object.values(dailyAllocations).reduce((acc, day) => acc + (day.amount || 0), 0) || 1;
        
        // Calculate share percentage
        return ((totalConfirmed + totalProjected) / totalSol) * 100;
    };

    // Update auction state after each contribution
    useEffect(() => {
        if (wallet.connected) {
            // Calculate new auction stats
            const newAuctionStats = calculateAuctionStats();
            
            // Force a re-render by creating a new state object
            setState(prevState => {
                const updatedState = {
                    ...prevState,
                    totalSolRaised: newAuctionStats.totalSolRaised,
                    auctionState: {
                        ...prevState.auctionState,
                        totalSolRaised: newAuctionStats.totalSolRaised,
                        totalSold: newAuctionStats.totalSolRaised,
                        totalParticipants: newAuctionStats.totalParticipants
                    }
                };
                
                console.log("Updated auction state after contribution:", {
                    totalSolRaised: updatedState.totalSolRaised,
                    auctionStateTotalSolRaised: updatedState.auctionState.totalSolRaised,
                    totalParticipants: updatedState.auctionState.totalParticipants
                });
                
                return updatedState;
            });
        }
    }, [userContributions, wallet.connected]);

    return (
        <AuctionContext.Provider value={{
            wallet,
            totalSolRaised: state.stats.totalSolRaised,
            currentPrice: state.stats.currentPrice,
            dexPrice: state.stats.dexPrice,
            moonshot: state.stats.moonshot,
            marketCap: state.stats.marketCap,
            startPrice: START_PRICE,
            circulatingSupply: CIRCULATING_SUPPLY,
            rewardsPool,
            currentDay: selectedDay,
            startDate: AUCTION_START_DATE,
            TOTAL_AUCTION_TOKENS,
            TOTAL_DAYS,
            SOL_PRICE_USD,
            DAILY_ALLOCATION,
            handleContribute,
            handleReserveAllDays,
            getDayStats,
            calculateProjectedStats,
            clearProjectedContributions,
            pendingContribution,
            projectedContributions,
            isDemo,
            toggleDemoMode,
            getUserStats,
            auctionState: {
                ...auctionState,
                dailyAllocations: Object.values(auctionState.dailyAllocations).map(allocation => ({
                    ...allocation,
                    userContributions: auctionState.userContributions[allocation.day] || [],
                    allocation: getDailyAllocation(allocation.day)
                }))
            }
        }}>
            {children}
        </AuctionContext.Provider>
    );
}
